import { toast } from "sonner";
import { CurbZoneHover, MapType, Restriction, RestrictionHover, ViewportPlaceType } from "types";
import { EditableGeoJsonLayer, SelectionLayer, ViewMode, EditAction } from "@deck.gl-community/editable-layers";
import { Feature, FeatureCollection, LineString, Polygon, Position } from "geojson";
import { ModeViewType, getPolygonColor, toLineStringFeature } from "constants/map.const";
import {
  RestrictionModeType,
  curbZoneRestrictionMode,
  multipleRestrictionMode,
  trafficRestrictionMode,
} from "../components/map-tool-box/restriction-tool-box-entry.const";
import { calculateArea, convertLineToPolygon, getPlace } from "helper/format-data";
import { cloneDeep, omit } from "lodash";
import { convertDateToUTC, parseISOString } from "helper/date-time";
import { filterFeatureIndex, filterLineStringFromPoint } from "helper/map-utils";
import { getQueryParams, isNil } from "helper/utils";
import { isTrafficRestriction, restrictionGeoJsonLayerModes } from "../restriction.const";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { CityContext } from "contexts/city-context-provider";
import { Decree } from "types/decree.type";
import type { DeleteRestrictionMutationVariables } from "@/types/gql/graphql";
import { GeoJsonLayer } from "@deck.gl/layers";
import { PickingInfo } from "deck.gl";
import { Range } from "react-date-range";
import { ViewState } from "react-map-gl/mapbox";
import { WebMercatorViewport } from "@math.gl/web-mercator";
import { bbox } from "@turf/turf";
import { findObject } from "helper/array";
import { skipToken } from "@apollo/client/react";
import { uniqBy } from "lodash";
import { useInteractMap } from "hooks/use-interact-map";
import { usePressKeyboard } from "hooks/use-press-keyboard";
import { useQueryRestriction } from "./use-query-restriction";
import { useQueryTemporaryDecree } from "@/modules/restriction/hooks/use-query-decree-file";
import useToggle from "hooks/use-toggle";

type RestrictionProps = {
  appMode: RestrictionModeType;
  nearBy?: number[][];
  selectedDate: Range;
  selectedDecree?: Decree | null;
};

export const useRestriction = ({ appMode, selectedDate }: RestrictionProps) => {
  const { view } = getQueryParams();
  const { currentCity, viewport, setViewport } = useContext(CityContext);
  const [markerPosition, setMarkerPosition] = useState<ViewportPlaceType | null>(null);
  const [restrictionEditionData, setRestrictionEditionData] = useState<FeatureCollection<LineString, Restriction>>({
    type: "FeatureCollection",
    features: [],
  });
  const [multiRestrictionEditionData, setMultiRestrictionEditionData] = useState<
    FeatureCollection<LineString, Restriction>
  >({
    type: "FeatureCollection",
    features: [],
  });
  const [currentLineParkingList, setCurrentLineParkingList] = useState<Feature<Polygon>[]>([]);
  const [currentLineRestriction, setCurrentLineRestriction] = useState<Feature<LineString, Restriction>[]>([]);
  const [curbZonePolygonData, setCurbZonePolygonData] = useState<Feature<Polygon>[]>([]);

  const [curbZoneDetail, setCurbZoneDetail] = useState<Feature<Polygon>[]>([]);
  const [openRestrictionModal, setOpenRestrictionModal] = useToggle(false);
  const [openCurbZoneRestrictionMultipleModal, setOpenCurbZoneRestrictionMultipleModal] = useState(false);

  const [mapType, setMapType] = useState<MapType>(MapType.MAPBOX);

  const currentPosition = useRef<Position>([]);
  const wrapMapRef = useRef<HTMLDivElement | null>(null);
  const preZooming = useRef<ViewState>(null);
  const previousAppMode = useRef<RestrictionModeType>(appMode);

  const [modeView, setModeView] = useState<ModeViewType>("normal");

  const preCurbZoneEdited = useRef<Feature<Polygon>>();
  const [restrictionByDecree, setRestrictionByDecree] = useState<Restriction[]>();
  const [selectedDecree, setSelectedDecree] = useState<Decree | null>(null);
  const [restrictionHover, setRestrictionHover] = useState<RestrictionHover | null>(null);
  const [curbZoneHover, setCurbZoneHover] = useState<CurbZoneHover | null>(null);

  const [trafficRestrictionEditionData, setTrafficRestrictionEditionData] = useState<
    FeatureCollection<LineString, Restriction>
  >({
    type: "FeatureCollection",
    features: [],
  });

  const [restrictionDetail, setRestrictionDetail] = useState<Feature<LineString, Restriction>[]>([]);

  const [isShowCurbZoneRestriction, setIsShowCurbZoneRestriction] = useToggle(true);

  const options = useMemo(() => {
    if (!selectedDecree?.id || !currentCity?.id) {
      return skipToken;
    }
    return {
      id: selectedDecree.id,
      cityId: currentCity.id,
    };
  }, [currentCity?.id, selectedDecree?.id]);

  const { temporaryDecreeData, isTemporaryDecreeDataLoading } = useQueryTemporaryDecree(options);

  const currentRestrictionIndexes = useMemo(() => {
    if (
      restrictionDetail[0]?.properties?.type &&
      isTrafficRestriction(restrictionDetail[0].properties.type) &&
      trafficRestrictionMode.includes(appMode)
    ) {
      /**
       * type is a traffic restriction
       * */
      return filterFeatureIndex(trafficRestrictionEditionData, restrictionDetail);
    } else if (curbZoneRestrictionMode.includes(appMode)) {
      return filterFeatureIndex(restrictionEditionData, restrictionDetail);
    }
    return [];
  }, [trafficRestrictionEditionData, restrictionEditionData, restrictionDetail, appMode]);

  const editableGeoJsonLayerMode = useMemo(() => {
    if (trafficRestrictionMode.includes(appMode)) {
      return restrictionGeoJsonLayerModes[appMode];
    }
    return ViewMode;
  }, [appMode]);

  const {
    loading,
    setBounding,
    data,
    createRestriction,
    createRestrictionWithoutRefetch,
    updateRestriction,
    updateMultiRestriction,
    removeRestriction,
    removeMultiRestriction,
    refetch,
  } = useQueryRestriction({
    selectedDate,
  });

  const { curbZones, restrictionsByBounding: restrictionList } = data;

  const { isSpaceBarPressed, isShiftPressed, isAltPressed } = usePressKeyboard({
    appMode,
    multipleMode: multipleRestrictionMode,
  });

  const restrictionToolTipData = useMemo(() => {
    if (!restrictionHover && !curbZoneHover) {
      return null;
    }

    let left = 0;
    let top = 0;
    if (restrictionHover) {
      left = restrictionHover.x;
      top = restrictionHover.y;
    } else if (curbZoneHover) {
      left = curbZoneHover.x;
      top = curbZoneHover.y;
    }

    return {
      restrictionHover: restrictionHover,
      curbZoneHover: curbZoneHover,
      x: left,
      y: top,
    };
  }, [restrictionHover, curbZoneHover]);

  const { onViewStateChange, handleZooming, getCurbZones } = useInteractMap({
    setBounding,
    viewport,
    preZooming,
  });

  useEffect(() => {
    if (!currentCity) {
      return;
    }
    const newViewport = {
      ...viewport,
      height: window.innerHeight,
      width: window.innerWidth,
    };
    getCurbZones(newViewport);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewport, currentCity]);

  const multiRestrictionsPolygonData = useMemo(() => {
    return multiRestrictionEditionData.features.map((item) => ({
      type: "Feature",
      geometry: item.properties?.geometry,
      properties: item.properties,
    })) as Feature<Polygon, Restriction>[];
  }, [multiRestrictionEditionData]);

  useEffect(() => {
    if (isNil(currentCity)) return;
    if (currentCity?.position) {
      currentPosition.current = currentCity?.position;
    }

    resetRestrictionDataOfEditState();
    setRestrictionByDecree(undefined);
  }, [currentCity]);

  useEffect(() => {
    if (!currentCity || loading) {
      return;
    }

    const newTrafficRestrictions: Feature<LineString, Restriction>[] = [];
    const newCurbZoneRestriction: Feature<LineString, Restriction>[] = [];
    restrictionList.forEach((restriction) => {
      const timeSpans = restriction.timeSpans?.map((item) => {
        const startDate = item.startDate ? parseISOString(item.startDate) : null;
        const endDate = item.endDate ? parseISOString(item.endDate) : null;
        return {
          ...item,
          startDate,
          endDate,
        };
      });
      const newRestriction = { ...restriction, timeSpans };
      if (isTrafficRestriction(restriction.type)) {
        /**
         * type is a traffic restriction
         * */
        newTrafficRestrictions.push(toLineStringFeature(newRestriction));
      } else {
        newCurbZoneRestriction.push(toLineStringFeature(newRestriction));
      }
    });
    setRestrictionEditionData({
      type: "FeatureCollection",
      features: newCurbZoneRestriction,
    });
    setTrafficRestrictionEditionData({
      type: "FeatureCollection",
      features: newTrafficRestrictions,
    });
  }, [restrictionList, loading, currentCity]);

  const cachedCurbZoneRestriction = useMemo((): FeatureCollection<LineString, Restriction> => {
    if (isShowCurbZoneRestriction) {
      return restrictionEditionData;
    }
    return {
      type: "FeatureCollection",
      features: [],
    };
  }, [isShowCurbZoneRestriction, restrictionEditionData]);

  const CurbZoneRestrictionsPolygonData = useMemo(
    () =>
      cachedCurbZoneRestriction.features.map((item) => ({
        type: "Feature",
        geometry: item.properties?.geometry,
        properties: item.properties,
        id: item.id,
      })) as Feature<Polygon, Restriction>[],
    [cachedCurbZoneRestriction],
  );

  const cachedTrafficRestriction = useMemo((): FeatureCollection<LineString, Restriction> => {
    if (isShowCurbZoneRestriction) {
      return trafficRestrictionEditionData;
    }
    return {
      type: "FeatureCollection",
      features: [],
    };
  }, [isShowCurbZoneRestriction, trafficRestrictionEditionData]);

  const trafficRestrictionsPolygonData = useMemo(
    () =>
      cachedTrafficRestriction.features.map((item) => ({
        type: "Feature",
        geometry: item.properties?.geometry,
        properties: item.properties,
        id: item.id,
      })) as Feature<Polygon, Restriction>[],
    [cachedTrafficRestriction],
  );

  useEffect(() => {
    if (!currentCity || loading) {
      return;
    }

    const polygonData = curbZones.map((curbZone) => {
      return {
        id: curbZone.id,
        curbZoneId: curbZone.curbZoneId,
        geometry: curbZone.geometry,
        type: "Feature",
        properties: { ...curbZone },
      } as Feature<Polygon>;
    });
    setCurbZonePolygonData(polygonData);
  }, [curbZones, loading, currentCity]);

  const moveMapToCurbZoneRestrictions = (listCurbZoneRestrictions: Restriction[] | undefined) => {
    if (listCurbZoneRestrictions && listCurbZoneRestrictions?.length > 0) {
      if (!wrapMapRef.current) {
        return;
      }
      /**
       * move map to selected restrictions
       * **/
      const [minLng, minLat, maxLng, maxLat] = bbox({
        type: "FeatureCollection",
        features: listCurbZoneRestrictions.map((item) => ({
          type: "Feature",
          geometry: item.geometry,
          properties: item,
        })),
      });
      const newViewport = new WebMercatorViewport({
        ...viewport,
        height: wrapMapRef.current?.clientHeight,
        width: wrapMapRef.current?.clientWidth,
      });
      const { longitude, latitude, zoom } = newViewport.fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat],
        ],
        { padding: 2 },
      );
      setViewport({
        ...viewport,
        latitude,
        longitude,
        zoom: Math.min(zoom, 20),
        padding: {
          top: 2,
          right: 2,
          bottom: 2,
          left: 2,
        },
      });
    } else if (view) {
      const currentView = view?.split(",");
      setViewport((prevState) => ({
        ...prevState,
        latitude: Number(currentView[0]),
        longitude: Number(currentView[1]),
        zoom: Number(currentView[2]),
      }));
    }
  };

  useEffect(() => {
    if (isTemporaryDecreeDataLoading || !temporaryDecreeData || !selectedDecree) {
      return;
    }
    const newRestrictionByDecree = temporaryDecreeData.restrictions?.map((item) => ({
      ...item,
      decrees: [selectedDecree],
    })) as Restriction[];
    moveMapToCurbZoneRestrictions(newRestrictionByDecree);
    setRestrictionByDecree(newRestrictionByDecree);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [temporaryDecreeData, isTemporaryDecreeDataLoading, selectedDecree]);

  useEffect(() => {
    /***
     * Don't reset data when appMode switches between three modes: view mode, editTrafficRestriction mode, and EditCurbZoneRestriction mode.
     * **/
    const shouldResetData = !(
      (appMode === RestrictionModeType.View &&
        [RestrictionModeType.EditTrafficRestriction, RestrictionModeType.EditCurbZoneRestriction].includes(
          previousAppMode.current,
        )) ||
      ([RestrictionModeType.EditTrafficRestriction, RestrictionModeType.EditCurbZoneRestriction].includes(appMode) &&
        (previousAppMode.current === RestrictionModeType.View ||
          [RestrictionModeType.EditTrafficRestriction, RestrictionModeType.EditCurbZoneRestriction].includes(
            previousAppMode.current,
          )))
    );

    if (shouldResetData) {
      resetRestrictionDataOfEditState();
      cancelCreateMultiple();
    }
    previousAppMode.current = appMode;
    /**
     * Press Esc key to select multi object
     * **/
    const handleKeyDownEsc = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        cancelCreateMultiple();
      }
    };

    if (!multipleRestrictionMode.includes(appMode)) {
      window.removeEventListener("keydown", (evt) => handleKeyDownEsc(evt));
      return;
    }
    window.addEventListener("keydown", (evt) => handleKeyDownEsc(evt));
    return () => {
      window.removeEventListener("keydown", (evt) => handleKeyDownEsc(evt));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appMode]);

  const onEditDisableCurbZoneLayer = async ({
    updatedData,
    editType,
  }: EditAction<FeatureCollection<LineString, Restriction>>) => {
    if (appMode === RestrictionModeType.MultiEditRestriction) {
      return;
    }
    let curbZoneRestrictionIndex = currentRestrictionIndexes[0];
    if (appMode === RestrictionModeType.EditCurbZoneRestriction) {
      /** Re-calculate the polygon when changing the shape of curb-zone */
      const clonedFeature = cloneDeep(updatedData.features[curbZoneRestrictionIndex]) as Feature<
        LineString,
        Restriction
      >;
      const newPolygon = convertLineToPolygon(clonedFeature, clonedFeature.properties.width);
      updatedData.features[curbZoneRestrictionIndex].properties.geometry = newPolygon.geometry;
    }
    setRestrictionEditionData(updatedData);
    if (editType === "finishMovePosition" || editType === "addFeature") {
      if (appMode === RestrictionModeType.AddCurbZoneRestriction) {
        curbZoneRestrictionIndex = updatedData.features.length - 1;
        if (!curbZoneDetail[0]?.id) {
          return;
        }
      }

      const newLineParking = await getPlace<Restriction>(updatedData.features[curbZoneRestrictionIndex], {
        getCalculatedSpaces: true,
      });
      const currentRestrictionDetail =
        appMode === RestrictionModeType.AddCurbZoneRestriction
          ? updatedData.features[curbZoneRestrictionIndex].properties
          : restrictionDetail[0].properties;

      const newLineRestriction = {
        geometry: newLineParking.geometry,
        properties: {
          editLine: newLineParking?.properties?.editLine,
          geoPoint2d: newLineParking?.properties?.geoPoint2d,
          timeSpans: currentRestrictionDetail?.timeSpans,
          description: currentRestrictionDetail?.description,
          curbZoneId: currentRestrictionDetail.curbZoneId ?? curbZoneDetail[0]?.properties?.curbZoneId,
          geometry: newLineParking?.properties?.geometry,
          cityId: currentCity?.id,
          decreeIds: currentRestrictionDetail.decreeIds,
          decrees: currentRestrictionDetail.decrees,
          id: currentRestrictionDetail.id,
          width: currentRestrictionDetail.width,
          calculatedSpaces: newLineParking.properties.calculatedSpaces,
          type: newLineParking?.properties?.type,
          length: Number(newLineParking?.properties?.length),
          streetName: newLineParking.properties?.streetName,
          streetNumberEnd: newLineParking.properties?.streetNumberEnd,
          streetNumberStart: newLineParking.properties?.streetNumberStart,
        },
        type: "Feature",
        id: currentRestrictionDetail.id,
      } as Feature<LineString, Restriction>;
      setCurrentLineRestriction([newLineRestriction]);
      if (appMode === RestrictionModeType.AddCurbZoneRestriction) {
        setOpenRestrictionModal();
      } else {
        await handleSaveRestriction(newLineRestriction.properties, newLineRestriction);
      }
    }
  };

  const ableToEditCurbZoneRestriction = useMemo(() => {
    if (appMode === RestrictionModeType.MultiCreateCurbZoneRestriction) {
      return ViewMode;
    }
    if (curbZoneRestrictionMode.includes(appMode)) {
      return restrictionGeoJsonLayerModes[appMode];
    }
    return ViewMode;
  }, [appMode]);

  const handleHoverCurbZone = useCallback(
    (info: PickingInfo) => {
      if (info.object && appMode === RestrictionModeType.View) {
        setCurbZoneHover({
          x: info.x,
          y: info.y,
          properties: info.object.properties,
        });
      } else {
        setCurbZoneHover(null);
      }
    },
    [appMode],
  );

  const handleHoverRestriction = useCallback(
    (info: PickingInfo) => {
      if (info.object && appMode === RestrictionModeType.View) {
        setRestrictionHover({
          x: info.x,
          y: info.y,
          properties: info.object.properties,
        });
      } else {
        setRestrictionHover(null);
      }
    },
    [appMode],
  );

  const curbZonePolygonLayer = new GeoJsonLayer({
    id: "curb-zone-polygon",
    data: curbZonePolygonData,
    stroked: true,
    pickable: ![RestrictionModeType.EditCurbZoneRestriction, RestrictionModeType.MultiEditRestriction].includes(
      appMode,
    ),
    getFillColor: (feature) => getPolygonColor(feature, "fillColor"),
    getLineColor: (feature) => getPolygonColor(feature, "lineColor"),
    lineWidthMaxPixels: 1,
    onHover: handleHoverCurbZone,
    updateTriggers: {
      /**
       * Update appMode values in onHover function
       * **/
      onHover: handleHoverCurbZone,
    },
    opacity: 0.2,
  });

  const curbZoneRestrictionLayer = new EditableGeoJsonLayer({
    id: "curb-zone-restriction",
    data: {
      type: cachedCurbZoneRestriction.type,
      features: cachedCurbZoneRestriction.features,
    },
    selectedFeatureIndexes: currentRestrictionIndexes,
    mode: ableToEditCurbZoneRestriction,
    onEdit: (updatedData) =>
      onEditDisableCurbZoneLayer(updatedData as EditAction<FeatureCollection<LineString, Restriction>>),
    visible: curbZoneRestrictionMode.includes(appMode),
  });

  const multiRestrictionPolygonLayer = new GeoJsonLayer({
    id: "multi-disable-polygon-layer",
    data: multiRestrictionsPolygonData,
    stroked: true,
    pickable: false,
    getFillColor: [185, 28, 28, 56],
    getLineColor: [185, 28, 28, 100],
    lineWidthMaxPixels: 1,
    visible: appMode === RestrictionModeType.MultiCreateCurbZoneRestriction,
  });

  const restrictionPolygonLayer = new GeoJsonLayer({
    id: "disable-polygon-layer",
    data: CurbZoneRestrictionsPolygonData,
    stroked: true,
    pickable: [RestrictionModeType.View, RestrictionModeType.MultiEditRestriction].includes(appMode),
    getFillColor: [185, 28, 28, 56],
    getLineColor: function (feature) {
      if (
        [
          RestrictionModeType.View,
          RestrictionModeType.EditCurbZoneRestriction,
          RestrictionModeType.EditTrafficRestriction,
        ].includes(appMode) &&
        restrictionDetail[0] &&
        feature.id === restrictionDetail[0].id
      ) {
        return [255, 255, 28, 100];
      }
      return [185, 28, 28, 100];
    },
    lineWidthMaxPixels: 3,
    onHover: (info) => {
      handleHoverRestriction(info);
    },
    opacity: multipleRestrictionMode.includes(appMode) ? 0.1 : 1,
    updateTriggers: {
      getLineColor: restrictionDetail,
    },
    visible: isShowCurbZoneRestriction,
  });

  const multipleCurbZoneRestrictionLayer = new EditableGeoJsonLayer({
    id: "multi-curb-zone-restriction",
    data: {
      type: multiRestrictionEditionData.type,
      features: multiRestrictionEditionData.features,
    },
    selectedFeatureIndexes: currentRestrictionIndexes,
    mode: ViewMode,
    visible: curbZoneRestrictionMode.includes(appMode),
  });

  const currentCityLayer = new GeoJsonLayer({
    id: "current-city",
    data: currentCity?.boundaries,
    filled: false,
    lineWidthMinPixels: 1,
    lineWidthMaxPixels: 4,
  });

  const selectMultipleCurbZoneRestriction = ({ pickingInfos }: { pickingInfos: PickingInfo[] }) => {
    if (pickingInfos.length > 0) {
      const restrictionData = filterLineStringFromPoint(pickingInfos);
      const newRestrictions = [...restrictionDetail, ...restrictionData];
      const uniqArray = uniqBy(newRestrictions, "id");
      setRestrictionDetail(uniqArray);
    }
  };

  const selectMultipleCurbZone = ({ pickingInfos }: { pickingInfos: PickingInfo[] }) => {
    if (pickingInfos.length > 0) {
      mergedCurbZoneRestrictions(pickingInfos);
    }
  };

  const mergedCurbZoneRestrictions = (pickingInfos: PickingInfo[]) => {
    /**
     * Delete duplicate data when press shift
     * **/
    const selectedCurbZones = convertCurbZoneToCurbZoneRestriction(pickingInfos);

    const newRestrictions = [...multiRestrictionEditionData.features, ...selectedCurbZones];
    let uniqArray = uniqBy(newRestrictions, "id");

    if (isShiftPressed) {
      const fCurbZone = findObject(multiRestrictionEditionData.features, "id", pickingInfos[0]?.object?.id);
      if (fCurbZone > -1) {
        uniqArray = uniqArray.filter((item) => item.id !== pickingInfos[0]?.object?.id);
      }
    }
    setMultiRestrictionEditionData({
      type: "FeatureCollection",
      features: uniqArray,
    });
    setRestrictionDetail(uniqArray);
  };

  const convertCurbZoneToCurbZoneRestriction = (infos: PickingInfo[]) => {
    if (infos && infos.length > 0) {
      return infos.reduce<Feature<LineString, Restriction>[]>((filteredArray, info) => {
        /**
         * Only create new curb zone restriction if the selected curb zone hasn't any curb zone restriction
         * **/
        if (info && info?.object?.id) {
          return [
            ...filteredArray,
            {
              geometry: info.object?.properties?.editLine.geometry,
              properties: {
                id: info.object.id,
                editLine: info.object?.properties?.editLine,
                geoPoint2d: info.object?.properties?.geoPoint2d,
                calculatedSpaces: info.object?.properties?.calculatedSpaces,
                length: info.object?.properties?.length,
                startDate: null,
                endDate: null,
                description: "",
                curbZoneId: info.object.curbZoneId,
                geometry: info.object?.properties?.geometry,
                cityId: currentCity?.id ?? "",
                decreeIds: [],
                width: info.object.properties.width,
                streetName: info.object.properties.streetName,
                streetNumberStart: info.object.properties.streetNumberStart,
                streetNumberEnd: info.object.properties.streetNumberEnd,
              },
              id: info.object.id,
              type: "Feature",
              curbZoneId: info.object.curbZoneId,
            },
          ];
        }
        return filteredArray;
      }, []);
    }
    return [];
  };

  const selectionCurbZoneType = useMemo(() => {
    if (isShiftPressed || appMode !== RestrictionModeType.MultiCreateCurbZoneRestriction) {
      return "none";
    }
    return isAltPressed ? "rectangle" : "polygon";
  }, [isAltPressed, isShiftPressed, appMode]);

  const selectionCurbZoneRestrictionType = useMemo(() => {
    if (isShiftPressed || appMode !== RestrictionModeType.MultiEditRestriction) {
      return "none";
    }
    return isAltPressed ? "rectangle" : "polygon";
  }, [isAltPressed, isShiftPressed, appMode]);

  const selectionMultipleCurbZoneLayer = new SelectionLayer({
    id: "selection-rectangle",
    selectionType: selectionCurbZoneType,
    layerIds: ["curb-zone-polygon"],
    onSelect: selectMultipleCurbZone,
    lineWidthMinPixels: 1,
    visible: appMode === RestrictionModeType.MultiCreateCurbZoneRestriction && !isSpaceBarPressed && !isShiftPressed,
  });

  const selectionMultipleCurbZoneRestrictionLayer = new SelectionLayer({
    id: "selection-rectangle-curb-zone-restriction",
    selectionType: selectionCurbZoneRestrictionType,
    layerIds: ["curb-zone-restriction"],
    onSelect: selectMultipleCurbZoneRestriction,
    lineWidthMinPixels: 1,
    visible: appMode === RestrictionModeType.MultiEditRestriction && !isSpaceBarPressed && !isShiftPressed,
  });

  const showRestrictionDetail = useMemo(() => {
    if (isTrafficRestriction(restrictionDetail[0]?.properties?.type)) {
      /**
       * type is a traffic restriction
       * **/
      return (
        appMode === RestrictionModeType.EditTrafficRestriction && restrictionDetail && restrictionDetail.length === 1
      );
    } else {
      return (
        appMode === RestrictionModeType.EditCurbZoneRestriction && restrictionDetail && restrictionDetail.length === 1
      );
    }
  }, [appMode, restrictionDetail]);

  const restrictionData = useMemo(() => {
    if (!restrictionDetail[0] && (multipleRestrictionMode.includes(appMode) || !currentLineRestriction[0])) {
      return undefined;
    }

    return currentLineRestriction[0]?.properties ?? restrictionDetail[0].properties ?? undefined;
  }, [currentLineRestriction, restrictionDetail, appMode]);

  const showCurbZoneMultiDetail = useMemo(
    () =>
      appMode === RestrictionModeType.MultiCreateCurbZoneRestriction &&
      restrictionDetail &&
      restrictionDetail.length > 0,
    [appMode, restrictionDetail],
  );

  const showCurbZoneRestrictionMultiDetail = useMemo(
    () => appMode === RestrictionModeType.MultiEditRestriction && restrictionDetail && restrictionDetail.length > 0,
    [appMode, restrictionDetail],
  );

  const resetRestrictionDataOfEditState = () => {
    setCurrentLineRestriction([]);
    setCurbZoneDetail([]);
    setRestrictionDetail([]);
  };

  const getCurbZoneRestrictionLayers = () => {
    /**
     * Move curb zone layer up to restriction layer if app mode is not multi edit
     * **/
    if (appMode === RestrictionModeType.MultiCreateCurbZoneRestriction) {
      return [
        currentCityLayer,
        multipleCurbZoneRestrictionLayer,
        restrictionPolygonLayer,
        multiRestrictionPolygonLayer,
        curbZoneRestrictionLayer,
        curbZonePolygonLayer,
        selectionMultipleCurbZoneLayer,
        selectionMultipleCurbZoneRestrictionLayer,
      ];
    }
    if (appMode === RestrictionModeType.MultiEditRestriction) {
      return [
        currentCityLayer,
        multipleCurbZoneRestrictionLayer,
        curbZonePolygonLayer,
        curbZoneRestrictionLayer,
        restrictionPolygonLayer,
        multiRestrictionPolygonLayer,
        selectionMultipleCurbZoneLayer,
        selectionMultipleCurbZoneRestrictionLayer,
      ];
    }
    if (appMode === RestrictionModeType.AddCurbZoneRestriction) {
      return [
        currentCityLayer,
        multipleCurbZoneRestrictionLayer,
        restrictionPolygonLayer,
        multiRestrictionPolygonLayer,
        curbZoneRestrictionLayer,
        curbZonePolygonLayer,
        selectionMultipleCurbZoneLayer,
        selectionMultipleCurbZoneRestrictionLayer,
      ];
    }
    return [
      currentCityLayer,
      multipleCurbZoneRestrictionLayer,
      curbZonePolygonLayer,
      restrictionPolygonLayer,
      multiRestrictionPolygonLayer,
      curbZoneRestrictionLayer,
      selectionMultipleCurbZoneLayer,
      selectionMultipleCurbZoneRestrictionLayer,
    ];
  };

  const getCursor = () => {
    if (
      appMode === RestrictionModeType.EditCurbZoneRestriction ||
      appMode === RestrictionModeType.AddCurbZoneRestriction
    ) {
      if (currentLineParkingList.length === 0) {
        return ({ isDragging }: { isDragging: boolean }) => (isDragging ? "grabbing" : "grab");
      }
      return curbZoneRestrictionLayer.getCursor.bind(curbZoneRestrictionLayer);
    }
    return ({ isDragging }: { isDragging: boolean }) => (isDragging ? "grabbing" : "grab");
  };
  const selectAddress = (viewport: ViewportPlaceType | null) => {
    setMarkerPosition(viewport);
    if (viewport) {
      setViewport((prevState) => ({ ...prevState, ...viewport, zoom: 16 }));
    }
  };

  const handleSelectCurbZoneRestriction = (info: PickingInfo) => {
    if (appMode === RestrictionModeType.View || !info.object) {
      return;
    }
    if (appMode === RestrictionModeType.MultiCreateCurbZoneRestriction && isShiftPressed) {
      if (info.object) {
        mergedCurbZoneRestrictions([info]);
      } else {
        return;
      }
    } else if (appMode === RestrictionModeType.MultiEditRestriction && isShiftPressed) {
      const fCurbZoneRestriction = findObject(restrictionDetail, "id", info.object.id);
      if (fCurbZoneRestriction > -1) {
        const newCurbZoneRestrictions = restrictionDetail.filter(
          (item) => item.id !== restrictionDetail[fCurbZoneRestriction].id,
        );
        setRestrictionDetail(newCurbZoneRestrictions);
      } else {
        setRestrictionDetail([...restrictionDetail, info.object]);
        return;
      }
    } else if (appMode === RestrictionModeType.EditCurbZoneRestriction) {
      if (!info.object?.properties?.curbZoneId) {
        return;
      }
      setRestrictionDetail([info.object]);
      setCurrentLineRestriction([info.object]);
    } else if (curbZoneDetail.length === 0) {
      const fCurbZone = findObject(curbZonePolygonData, "id", info.object?.id);
      setCurbZoneDetail([curbZonePolygonData[fCurbZone]]);
      preCurbZoneEdited.current = curbZonePolygonData[fCurbZone];
      setCurrentLineParkingList([preCurbZoneEdited.current]);
    }
  };

  const handleDragEnd = (event: PickingInfo) => {
    if (multipleRestrictionMode.includes(appMode) && !isSpaceBarPressed) return;
    if (!event.viewport) {
      return;
    }
    const viewport = event.viewport as unknown as ViewState;
    if (viewport) {
      const newPosition = [viewport?.longitude, viewport?.latitude];
      currentPosition.current = [...newPosition];
      setViewport((prevState) => ({
        ...prevState,
        pitch: viewport.pitch,
        bearing: viewport.bearing,
        longitude: viewport.longitude,
        latitude: viewport.latitude,
        zoom: viewport.zoom,
      }));
    }
  };

  const handleRemoveRestriction = async (input: DeleteRestrictionMutationVariables) => {
    await removeRestriction({ variables: input });
    resetRestrictionDataOfEditState();
  };

  const handleRemoveMultipleCurbZoneRestrictions = async (input: DeleteRestrictionMutationVariables[]) => {
    const listIds = input.map((item) => item.id);
    await removeMultiRestriction({ variables: { ids: listIds } });
    resetRestrictionDataOfEditState();
  };

  const cancelEditRestriction = (isEdit = false) => {
    if (!isEdit) {
      const newParkingEditions = [...restrictionEditionData.features];
      newParkingEditions.pop();
      setRestrictionEditionData({ type: "FeatureCollection", features: newParkingEditions });
    }
    setRestrictionDetail([]);
    setCurrentLineParkingList([]);
    if (openRestrictionModal) setOpenRestrictionModal();
    setCurrentLineRestriction([]);
  };

  const handleSelectDecree = (input: Decree) => {
    if (input.id === selectedDecree?.id) {
      setRestrictionByDecree(undefined);
      setSelectedDecree(null);
    } else {
      setSelectedDecree(input);
    }
    resetRestrictionDataOfEditState();
  };

  const cancelCreateMultiple = () => {
    if (openRestrictionModal) {
      setOpenRestrictionModal();
    }
    setMultiRestrictionEditionData({
      type: "FeatureCollection",
      features: [],
    });
    setCurrentLineParkingList([]);
    setRestrictionDetail([]);
  };

  const cancelEditMultiple = () => {
    if (openCurbZoneRestrictionMultipleModal) {
      setOpenCurbZoneRestrictionMultipleModal(false);
    }
    setMultiRestrictionEditionData({
      type: "FeatureCollection",
      features: [],
    });
    setCurrentLineParkingList([]);
    setRestrictionDetail([]);
  };

  const handleSaveMultiCurbZoneRestriction = async (newRestrictionParam: Restriction[]) => {
    if (!multiRestrictionsPolygonData.length) {
      return;
    }

    // Create all mutations in parallel
    const createRestrictionPromises = newRestrictionParam.map((item) => {
      const itemWithoutId = omit(item, "id");
      return createRestrictionWithoutRefetch({
        variables: { input: itemWithoutId },
      });
    });

    await Promise.allSettled(createRestrictionPromises);

    // Refetch once after all mutations complete
    await refetch();

    resetRestrictionDataOfEditState();
  };
  const handleUpdateMultiCurbZoneRestriction = async (newRestrictionParam: Restriction[]) => {
    await updateMultiRestriction({ variables: { input: { restrictions: newRestrictionParam } } });
    resetRestrictionDataOfEditState();
  };

  const changeMultipleCurbZoneRestriction = async (disableProperties: Restriction) => {
    if (openCurbZoneRestrictionMultipleModal) {
      setOpenCurbZoneRestrictionMultipleModal(false);
    }
    const newCurbZoneRestrictions: Restriction[] = [];

    const timeSpans = disableProperties?.timeSpans?.map((item) => {
      /**
       * convert date object to UTC time
       * */
      const startDate = item.startDate ? convertDateToUTC(item.startDate) : null;
      const endDate = item.endDate ? convertDateToUTC(item.endDate) : null;
      return {
        ...item,
        startDate,
        endDate,
      };
    });

    for (const item of restrictionDetail) {
      const width = disableProperties.width ?? 2;
      /** Re-calculate the polygon when changing the shape of restrictions
       * Don't change the: streetNumberStart, streetNumberEnd, calculatedSpaces
       * */
      const editLineWithProperties = cloneDeep({ ...item.properties.editLine, properties: item.properties }) as Feature<
        LineString,
        Restriction
      >;
      const newPolygon = convertLineToPolygon(editLineWithProperties, width);
      const newRestrictionParam: Restriction = {
        geometry: newPolygon.geometry,
        timeSpans,
        description: disableProperties.description,
        decreeIds: disableProperties.decreeIds,
        geoPoint2d: item.properties.geoPoint2d,
        editLine: item.properties.editLine,
        curbZoneId: item.properties.curbZoneId,
        cityId: currentCity?.id ?? "",
        type: disableProperties.type,
        id: item?.id as string,
        streetName: disableProperties?.streetName,
        streetNumberStart: item.properties?.streetNumberStart,
        streetNumberEnd: item.properties?.streetNumberEnd,
        length: item.properties?.length,
        width,
        calculatedSpaces: item.properties.calculatedSpaces,
      };
      if (width !== item.properties.width) {
        /**
         * If changed width, re-calculate the spaces
         **/
        const curbZoneFeature = {
          type: "Feature",
          geometry: newRestrictionParam.editLine.geometry,
          properties: { ...newRestrictionParam, width },
        } as Feature<LineString, Restriction>;

        const { calculatedSpaces } = calculateArea(curbZoneFeature);
        newRestrictionParam.calculatedSpaces = calculatedSpaces;
      }
      newCurbZoneRestrictions.push(newRestrictionParam);
    }
    if (appMode === RestrictionModeType.MultiCreateCurbZoneRestriction) {
      await handleSaveMultiCurbZoneRestriction(newCurbZoneRestrictions);
    } else {
      await handleUpdateMultiCurbZoneRestriction(newCurbZoneRestrictions);
    }
  };

  const clickDescriptionRestriction = (restriction: Feature<Polygon, Restriction>) => {
    const fRestriction = restriction.id === restrictionDetail[0]?.id;
    if (fRestriction) {
      resetRestrictionDataOfEditState();
    } else {
      if (
        isTrafficRestriction(restriction.properties?.type) &&
        appMode !== RestrictionModeType.EditTrafficRestriction
      ) {
        /**
         * type is a traffic restriction
         * **/
        toast.warning("Passez au mode suivant: Modifier Restriction de traffic", {
          style: { color: "#ff0000" },
        });
      } else if (
        !isTrafficRestriction(restriction.properties?.type) &&
        appMode !== RestrictionModeType.EditCurbZoneRestriction
      ) {
        toast.warning("Passez au mode suivant: Modifier Restriction", {
          style: { color: "#ff0000" },
        });
      }
      //convert the polygon to lineString;
      const newLineString = {
        ...restriction,
        geometry: restriction.properties.editLine.geometry,
      };
      setRestrictionDetail([newLineString]);
      setCurrentLineRestriction([newLineString]);
      moveMapToCurbZoneRestrictions([restriction.properties]);
    }
  };

  const onEditStreetRestrictionLayer = async ({
    updatedData,
    editType,
  }: EditAction<FeatureCollection<LineString, Restriction>>) => {
    setTrafficRestrictionEditionData(updatedData);
    let curbZoneRestrictionIndex = currentRestrictionIndexes[0];
    if (appMode === RestrictionModeType.EditTrafficRestriction) {
      /** Re-calculate the polygon when changing the shape of curb-zone */
      const clonedFeature = cloneDeep(updatedData.features[curbZoneRestrictionIndex]) as Feature<
        LineString,
        Restriction
      >;
      const newPolygon = convertLineToPolygon(clonedFeature, clonedFeature.properties.width);
      updatedData.features[curbZoneRestrictionIndex].properties.geometry = newPolygon.geometry;
      setTrafficRestrictionEditionData(updatedData);
    } else {
      setTrafficRestrictionEditionData(updatedData);
    }
    if (editType === "finishMovePosition" || editType === "addFeature") {
      if (appMode === RestrictionModeType.AddTrafficRestriction) {
        curbZoneRestrictionIndex = updatedData.features.length - 1;
      }

      const newLineParking = await getPlace<Restriction>(updatedData.features[curbZoneRestrictionIndex]);
      const currentRestrictionDetail =
        appMode === RestrictionModeType.AddTrafficRestriction
          ? updatedData.features[curbZoneRestrictionIndex].properties
          : restrictionDetail[0].properties;

      const newLineTrafficRestriction = {
        geometry: newLineParking.geometry,
        properties: {
          editLine: newLineParking?.properties?.editLine,
          geoPoint2d: newLineParking?.properties?.geoPoint2d,
          timeSpans: currentRestrictionDetail?.timeSpans,
          description: currentRestrictionDetail?.description,
          geometry: newLineParking?.properties?.geometry,
          cityId: currentCity?.id,
          decreeIds: currentRestrictionDetail.decreeIds,
          id: currentRestrictionDetail.id,
          width: currentRestrictionDetail.width,
          calculatedSpaces: newLineParking.properties.calculatedSpaces,
          type: newLineParking?.properties?.type,
          length: Number(newLineParking.properties.length),
          streetName: newLineParking.properties?.streetName,
          streetNumberEnd: newLineParking.properties?.streetNumberEnd,
          streetNumberStart: newLineParking.properties?.streetNumberStart,
        },
        type: "Feature",
        id: currentRestrictionDetail.id,
      } as Feature<LineString, Restriction>;
      setCurrentLineRestriction([newLineTrafficRestriction]);
      if (appMode === RestrictionModeType.AddTrafficRestriction) {
        setOpenRestrictionModal();
      } else {
        await handleSaveRestriction(newLineTrafficRestriction.properties, newLineTrafficRestriction);
      }
    }
  };

  const streetRestrictionEditionLayer = new EditableGeoJsonLayer({
    id: "street-restriction-edition",
    data: {
      type: cachedTrafficRestriction.type,
      features: cachedTrafficRestriction.features as Feature<LineString>[],
    },
    selectedFeatureIndexes: currentRestrictionIndexes,
    mode: editableGeoJsonLayerMode,
    onEdit: (updatedData) =>
      onEditStreetRestrictionLayer(updatedData as EditAction<FeatureCollection<LineString, Restriction>>),
    visible: trafficRestrictionMode.includes(appMode),
  });

  const trafficRestrictionPolygonLayer = new GeoJsonLayer({
    id: "street-restriction-polygon",
    data: trafficRestrictionsPolygonData,
    stroked: true,
    pickable: appMode === RestrictionModeType.View,
    getFillColor: [185, 28, 28, 56],
    getLineColor: function (feature) {
      if (
        [
          RestrictionModeType.View,
          RestrictionModeType.EditCurbZoneRestriction,
          RestrictionModeType.EditTrafficRestriction,
        ].includes(appMode) &&
        restrictionDetail[0] &&
        feature.id === restrictionDetail[0].id
      ) {
        return [255, 255, 28, 100];
      }
      return [185, 28, 28, 100];
    },
    lineWidthMaxPixels: 3,
    onHover: (info) => {
      handleHoverRestriction(info);
    },
  });

  const trafficRestrictionLayers = [trafficRestrictionPolygonLayer, streetRestrictionEditionLayer];

  const handleSelectTrafficRestriction = (info: PickingInfo) => {
    if ([RestrictionModeType.View, RestrictionModeType.AddTrafficRestriction].includes(appMode)) {
      return;
    }
    if (!info.object || !isTrafficRestriction(info.object.properties.type)) {
      return;
    }

    setRestrictionDetail([info.object]);
    setCurrentLineRestriction([info.object]);
  };

  const handleSaveRestrictionError = () => {
    if (appMode === RestrictionModeType.AddCurbZoneRestriction) {
      setRestrictionEditionData({
        type: "FeatureCollection",
        features: [...restrictionEditionData.features.toSpliced(-1, 1)],
      });
    } else {
      setTrafficRestrictionEditionData({
        type: "FeatureCollection",
        features: [...trafficRestrictionEditionData.features.toSpliced(-1, 1)],
      });
    }
  };
  const handleSaveRestriction = async (
    restrictionProperties: Restriction,
    lineRestriction?: Feature<LineString, Restriction>,
  ) => {
    if (!currentLineRestriction.length || !currentLineRestriction[0].properties) {
      return;
    }
    const width = restrictionProperties?.width ?? 2;
    const clonedLineRestriction = cloneDeep(lineRestriction ?? currentLineRestriction[0]) as Feature<
      LineString,
      Restriction
    >;
    const newRestriction = convertLineToPolygon(clonedLineRestriction, width);

    const timeSpans = restrictionProperties?.timeSpans?.map((item) => {
      /**
       * convert date object to UTC time
       * */
      const startDate = item.startDate ? convertDateToUTC(item.startDate) : null;
      const endDate = item.endDate ? convertDateToUTC(item.endDate) : null;
      return {
        ...item,
        startDate,
        endDate,
      };
    });

    const newRestrictionParam: Omit<Restriction, "id"> = {
      geometry: newRestriction.geometry,
      timeSpans,
      description: restrictionProperties.description,
      decreeIds: restrictionProperties.decreeIds,
      geoPoint2d: newRestriction.properties.geoPoint2d,
      editLine: newRestriction.properties.editLine,
      curbZoneId: restrictionProperties.curbZoneId,
      cityId: currentCity?.id ?? "",
      type: restrictionProperties.type,
      length: restrictionProperties.length,
      width,
      calculatedSpaces: restrictionProperties.calculatedSpaces,
      streetName: restrictionProperties.streetName,
      streetNumberStart: restrictionProperties.streetNumberStart,
      streetNumberEnd: restrictionProperties.streetNumberEnd,
    };
    if (restrictionProperties.id) {
      await updateRestriction({
        variables: {
          input: { ...newRestrictionParam, id: restrictionProperties.id },
        },
      });
      setSelectedDecree(null);
      setRestrictionByDecree(undefined);
    } else {
      const res = await createRestriction({ variables: { input: newRestrictionParam } });
      if (!res.data?.createRestriction) {
        handleSaveRestrictionError();
      }
    }
    if (openRestrictionModal) {
      setOpenRestrictionModal();
      resetRestrictionDataOfEditState();
    }
  };

  const cancelEditTrafficRestriction = (isEdit = false) => {
    if (!isEdit) {
      const newParkingEditions = [...trafficRestrictionEditionData.features];
      newParkingEditions.pop();
      setTrafficRestrictionEditionData({ type: "FeatureCollection", features: newParkingEditions });
    }
    setRestrictionDetail([]);
    if (openRestrictionModal) {
      setOpenRestrictionModal();
    }
    setCurrentLineRestriction([]);
  };

  const resetHoverStates = () => {
    setRestrictionHover(null);
    setCurbZoneHover(null);
  };

  const mapLayers = [...getCurbZoneRestrictionLayers(), trafficRestrictionLayers];

  return {
    markerPosition,
    setMarkerPosition,
    selectAddress,
    viewport,
    mapType,
    setMapType,
    modeView,
    getCursor,
    getCurbZoneRestrictionLayers,
    handleDragEnd,
    handleSelectCurbZoneRestriction,
    handleZooming,
    onViewStateChange,
    setModeView,
    appMode,
    currentCity,
    currentLineRestriction,
    handleRemoveRestriction,
    setOpenRestrictionModal,
    setOpenCurbZoneRestrictionMultipleModal: setOpenCurbZoneRestrictionMultipleModal,
    openRestrictionModal,
    openCurbZoneRestrictionMultipleModal: openCurbZoneRestrictionMultipleModal,
    cancelEditRestriction,
    handleSaveRestriction,
    restrictionData,
    preZooming,
    selectedDate,
    selectedDecree,
    restrictionByDecree,
    handleSelectDecree,
    wrapMapRef,
    restrictionToolTipData,
    isSpaceBarPressed,
    showCurbZoneMultiDetail,
    showCurbZoneRestrictionMultiDetail,
    cancelCreateMultiple,
    cancelEditMultiple,
    handleRemoveMultipleCurbZoneRestrictions,
    clickDescriptionRestriction,
    resetRestrictionDataOfEditState,
    moveMapToCurbZoneRestrictions,
    cancelEditTrafficRestriction,
    handleSelectTrafficRestriction,
    showRestrictionDetail,
    trafficRestrictionLayers,
    restrictionDetail,
    mapLayers,
    changeMultipleCurbZoneRestriction,
    isShowCurbZoneRestriction: isShowCurbZoneRestriction,
    setIsShowCurbZoneRestriction: setIsShowCurbZoneRestriction,
    resetHoverStates,
  };
};
